// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useAuth } from './useAuth';
import { useShellStore, type Persona } from '../stores/shellStore';
import type { UserRole } from '../types';

/**
 * Maps a real auth role to the shell persona that drives the Carbon UI layout
 * (the mock's Operator / Ops Manager / Executive views).
 *
 * - operator            → Operator
 * - operations_manager  → Ops Manager
 * - admin               → Ops Manager (admin additionally keeps Users & Access;
 *                         see `isAdmin`)
 * - executive           → Executive
 */
export function personaForRole(role: UserRole | null): Persona {
  switch (role) {
    case 'operator':
      return 'operator';
    case 'executive':
      return 'executive';
    case 'operations_manager':
    case 'admin':
      return 'manager';
    default:
      return 'operator';
  }
}

export interface PersonaState {
  persona: Persona;
  effectiveRole: UserRole | null;
  /** Admin keeps the Users & Access surfaces on top of the Manager view. */
  isAdmin: boolean;
  /** True when the persona came from a manual/dev override rather than the role. */
  isOverridden: boolean;
}

/**
 * Resolve the active persona. A manual/dev override in the shell store wins;
 * otherwise the persona is derived from the authenticated role.
 */
export function usePersona(): PersonaState {
  const { effectiveRole, isAdmin } = useAuth();
  const override = useShellStore(s => s.personaOverride);

  return {
    persona: override ?? personaForRole(effectiveRole),
    effectiveRole,
    isAdmin,
    isOverridden: override != null,
  };
}
